import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    Sword,
    Target,
    Search,
    BookOpen,
    GraduationCap,
    Settings,
    Trophy,
    Swords,
} from 'lucide-react';

const mainActions = [
    {
        title: 'MULTIJUGADOR',
        icon: <Swords size={28} />,
        path: '/dashboard',
        color: 'hsl(215, 70%, 55%)',
        desc: 'Retos en línea',
    },
    {
        title: 'JUGAR VS IA',
        icon: <Sword size={28} />,
        path: '/play',
        color: 'hsl(168, 85%, 38%)',
        desc: 'vs. IA ajustada',
    },
    {
        title: 'ROMPECABEZAS',
        icon: <Target size={28} />,
        path: '/puzzles',
        color: 'hsl(25, 92%, 55%)',
        desc: 'Entrena táctica',
    },
    {
        title: 'ANÁLISIS',
        icon: <Search size={28} />,
        path: '/analysis',
        color: 'hsl(195, 80%, 55%)',
        desc: 'Analiza partidas',
    },
    {
        title: 'APERTURAS',
        icon: <BookOpen size={28} />,
        path: '/openings',
        color: 'hsl(265, 70%, 65%)',
        desc: 'Teoría profesional',
    },
    {
        title: 'APRENDER',
        icon: <GraduationCap size={28} />,
        path: '/learn',
        color: 'hsl(38, 92%, 55%)',
        desc: 'Cursos interactivos',
    },
    {
        title: 'AJUSTES',
        icon: <Settings size={28} />,
        path: '/settings',
        color: 'hsl(180, 20%, 60%)',
        desc: 'Configuración',
    },
];

const HomePage: React.FC = () => {
    const { user } = useAuth();

    return (
        <div className="flex flex-col h-full w-full max-w-[1200px] mx-auto overflow-hidden">
            <div className="flex-1 flex flex-col gap-2 lg:gap-4 overflow-y-auto lg:overflow-visible no-scrollbar pb-6">
                {/* Hero */}
                <div className="py-4 lg:py-6 flex flex-col items-center text-center group shrink-0">
                    <img
                        src="/logo-icon.png"
                        alt="ChessDrez Logo"
                        className="h-14 w-14 lg:h-[clamp(80px,20vh,160px)] lg:w-[clamp(80px,20vh,160px)] mb-2 lg:mb-4 object-contain drop-shadow-[0_0_15px_rgba(255,255,255,0.2)] transition-all duration-700 ease-out group-hover:scale-[1.12] group-hover:-rotate-3 group-hover:drop-shadow-[0_0_50px_rgba(255,255,255,0.5)]"
                    />
                    <h1 className="text-2xl lg:text-[clamp(2.5rem,8vh,4.5rem)] mb-0 lg:mb-2 font-black tracking-tighter leading-tight">
                        CHESS<span style={{ color: 'var(--primary-bright)' }}>DREZ</span>
                    </h1>
                    <p className="text-[var(--text-muted)] text-[8px] lg:text-[clamp(10px,1.2vh,14px)] font-bold uppercase tracking-widest opacity-80">
                        &gt; Domina cada movimiento
                    </p>
                </div>

                {/* Card Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-4 flex-1 min-h-0 content-start lg:content-center">
                    {mainActions.map((action, idx) => (
                        <Link
                            key={action.title}
                            to={action.path}
                            className={`
                                relative group/card flex flex-col gap-1 lg:gap-2 p-3 lg:p-[clamp(0.75rem,2vh,1.5rem)] rounded-[14px] lg:rounded-2xl border transition-all duration-300 overflow-hidden
                                ${idx === 0 ? 'col-span-2' : ''}
                            `}
                            style={{
                                borderColor: `${action.color}35`,
                                background: `linear-gradient(135deg, ${action.color}12, ${action.color}06)`,
                            }}
                            onMouseEnter={e => {
                                (e.currentTarget as HTMLElement).style.borderColor = `${action.color}70`;
                                (e.currentTarget as HTMLElement).style.boxShadow = `0 0 30px ${action.color}20`;
                            }}
                            onMouseLeave={e => {
                                (e.currentTarget as HTMLElement).style.borderColor = `${action.color}35`;
                                (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                            }}
                        >
                            {/* Accent glow */}
                            <div
                                className="absolute -top-8 -right-8 w-28 h-28 rounded-full opacity-10 blur-2xl pointer-events-none group-hover/card:opacity-20 transition-opacity duration-500"
                                style={{ background: action.color }}
                            />

                            {/* Icon box */}
                            <div
                                className="w-7 h-7 lg:w-[clamp(40px,6vh,56px)] lg:h-[clamp(40px,6vh,56px)] flex items-center justify-center rounded-xl shrink-0 transition-all duration-300 group-hover/card:scale-110 group-hover/card:shadow-xl"
                                style={{
                                    color: action.color,
                                    background: `${action.color}22`,
                                    boxShadow: `0 0 18px ${action.color}30`,
                                }}
                            >
                                <div className="scale-75 lg:scale-100 flex items-center justify-center">
                                    {action.icon}
                                </div>
                            </div>

                            {/* Text */}
                            <div className="flex flex-col gap-0.5">
                                <span className={`font-black tracking-tight leading-none ${idx === 0 ? 'text-xs lg:text-[clamp(1rem,2.5vh,1.25rem)]' : 'text-[10px] lg:text-[clamp(0.875rem,1.8vh,1rem)]'} text-white`}>
                                    {action.title}
                                </span>
                                <span className="text-[7px] lg:text-[clamp(8px,1vh,11px)] font-semibold uppercase tracking-widest opacity-50 mt-0.5" style={{ color: action.color }}>
                                    {action.desc}
                                </span>
                            </div>

                            {/* Featured badge for first item */}
                            {idx === 0 && (
                                <div
                                    className="absolute top-2 right-2 text-[6px] lg:text-[clamp(6px,0.8vh,8px)] font-black px-1.5 py-0.5 rounded-full uppercase tracking-widest"
                                    style={{ background: action.color, color: '#000' }}
                                >
                                    JUGAR AHORA
                                </div>
                            )}
                        </Link>
                    ))}
                </div>

                {/* Guest Banner */}
                {!user && (
                    <div className="mt-2 lg:mt-4 p-4 lg:p-[clamp(1rem,3vh,2rem)] rounded-2xl bg-gradient-to-r from-[var(--primary)]/20 to-transparent border border-[var(--primary)]/30 relative overflow-hidden group/banner shrink-0">
                        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                            <div className="flex flex-col gap-1">
                                <h2 className="text-lg lg:text-[clamp(1.25rem,4vh,1.75rem)] font-black text-white leading-tight">
                                    ¡Inicia sesión para <span className="text-[var(--primary-bright)]">guardar</span> tus partidas! ♟️
                                </h2>
                                <p className="text-[var(--text-muted)] text-[10px] lg:text-[clamp(12px,1.5vh,14px)] font-medium max-w-xl hidden sm:block">
                                    Registrate gratis para tener acceso a tu historial completo y ranking mundial.
                                </p>
                            </div>
                            <div className="flex items-center gap-3">
                                <Link
                                    to="/register"
                                    className="px-6 py-3 lg:px-8 lg:py-4 rounded-xl bg-[var(--primary)] text-white text-xs lg:text-sm font-black tracking-widest uppercase hover:bg-[var(--primary-bright)] shadow-xl shadow-[var(--primary-glow)] transition-all hover:scale-105"
                                >
                                    Registro
                                </Link>
                                <Link
                                    to="/login"
                                    className="px-6 py-3 lg:px-8 lg:py-4 rounded-xl border border-white/10 text-white text-xs lg:text-sm font-black tracking-widest uppercase hover:bg-white/5 transition-all"
                                >
                                    Entrar
                                </Link>
                            </div>
                        </div>
                        <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-[var(--primary)] opacity-10 blur-3xl rounded-full pointer-events-none" />
                    </div>
                )}

                {/* Footer stat */}
                <div className="hidden lg:flex items-center justify-center gap-2 py-2 opacity-30 shrink-0">
                    <Trophy size={14} className="text-yellow-400 shrink-0" />
                    <span className="text-[10px] font-black text-[var(--text-muted)] tracking-widest uppercase">
                        Más de 12,000 puzzles para entrenar
                    </span>
                </div>
            </div>
        </div>
    );
};

export default HomePage;
